import * as React from "react";
import {useRef, useState} from "react";
import {defaultRangeExtractor, useVirtualizer} from "@tanstack/react-virtual";
import {FilterSet, MenuNode, MenuNodeCfgSet, StaffUtilisationDataSet, Task} from "./types.ts";
import {UtilisationGraph} from "./UtilisationGraph.tsx";
import {
    closestCenter,
    DndContext,
    DragOverlay,
    getClientRect,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    restrictToVerticalAxis,
    restrictToFirstScrollableAncestor,
} from '@dnd-kit/modifiers';
import {arrayMove, SortableContext, verticalListSortingStrategy,} from '@dnd-kit/sortable';
import {SortableUtilisationGridRow} from "./SortableUtilisationGridRow.tsx";
import {UtilisationGridRow} from "./UtilisationGridRow.tsx";
import {Staff} from "../Components/BaseTypes.ts";
// import {getTransformAgnosticClientRect} from "@dnd-kit/core/dist/utilities";

// import dayjs from "dayjs";
// import utc from "dayjs/plugin/utc";
// import timezone from "dayjs/plugin/timezone";
// dayjs.extend(utc);
// dayjs.extend(timezone);
// dayjs.tz.setDefault('Pacific/Auckland');

export const COL_WIDTH_BY_INTERVAL = {
    'daily' : 72,
    'weekly' : 72,
    'fortnightly' : 72,
    'monthly' : 92
}

interface PropTypes {
    utilisationData: StaffUtilisationDataSet,
    staff: Record<string, Staff>
    menuNodesFlatToRenderSet: MenuNode[][],
    // generateMenuNodesFlatToRender: (menuNodes: MenuNode[], filters) => MenuNode[][],
    filters: FilterSet,
    // setMenuNodesFlatToRender: React.Dispatch<React.SetStateAction<MenuNode[][]>>,
    menuNodeCfgSet: MenuNodeCfgSet,
    setMenuNodeCfgSet: React.Dispatch<React.SetStateAction<MenuNodeCfgSet>>,
    tasks: Record<number, Task>,
    staffScheduleOnly: boolean
}

export function VirtualisedGrid({
                                    utilisationData,
                                    filters,
                                    staff,
                                    menuNodesFlatToRenderSet,
                                    menuNodeCfgSet,
                                    setMenuNodeCfgSet,
                                    tasks,
    staffScheduleOnly
                                }: PropTypes) {
    // console.log('render grid');
    // const menuNodesFlat: MenuNode[] = useMemo(() => generateMenuNodesFlatToRender(menuNodes, filters), []);

    const menuNodesFlatToRender = menuNodesFlatToRenderSet[0];
    const menuNodesToGraph = menuNodesFlatToRenderSet[1];
    const [dragId, setDragId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    )

    const HEADING_COL_WIDTH = staffScheduleOnly ? 600 : 400;


    const parentRef = useRef();
    const rowVirtualizer = useVirtualizer({
        count: menuNodesFlatToRender.length + 1,
        getScrollElement: () => parentRef.current,
        estimateSize: (i) => i == menuNodesFlatToRender.length + 1 ? 50 : 23,
        // estimateSize: () => 23,
        rangeExtractor: React.useCallback(range => {
            const existingRange = defaultRangeExtractor(range);
            if (existingRange[0] != 0) existingRange.unshift(0);
            return existingRange;
        }, [])
    });

    const rangeRef = React.useRef({start: 0, end: 0});
    const columnVirtualizer = useVirtualizer({
        count: utilisationData.dateIndexes.length + 2,
        horizontal: true,
        isScrollingResetDelay: 500,
        getScrollElement: () => parentRef.current,
        estimateSize: (i) => i == 0 ? HEADING_COL_WIDTH : i == 1 ? 74 : COL_WIDTH_BY_INTERVAL[filters.interval],
        rangeExtractor: React.useCallback(range => {
            rangeRef.current = range;
            const existingRange = defaultRangeExtractor(range);
            if (existingRange[0] != 0) existingRange.unshift(0);
            return existingRange;
        }, [])
    });


    const graphRef = useRef();
    // let scrollOffset = 0;
    // let translateX = 0;
    const [graphXScroll, setGraphXScroll] = useState({
        x: 0,
        scroll: 0,
        columns: []
    });

    graphRef?.current?.scrollTo(graphXScroll.scroll, 0);

    React.useEffect(() => {
        if (!columnVirtualizer.isScrolling) {
            // console.log('not scrolling');
            const newCols = columnVirtualizer.getVirtualItems().slice(Math.floor(Math.min(columnVirtualizer.scrollOffset, HEADING_COL_WIDTH + 72) / COL_WIDTH_BY_INTERVAL[filters.interval])+1);
            setGraphXScroll({
                // x: newCols?.[1]?.start - (columnVirtualizer.scrollOffset > 400 ? 72 : 72),
                x: newCols?.[1]?.start - COL_WIDTH_BY_INTERVAL[filters.interval],
                // x: newCols?.[1]?.start,
                // x: columnVirtualizer.scrollOffset + 400 - (columnVirtualizer.scrollOffset > 400 ? 60 : 70),
                scroll: columnVirtualizer.scrollOffset,
                columns: newCols
            })
        }
    }, [columnVirtualizer.isScrolling])

    // console.log(columnVirtualizer);
    return (
        <>
            <div ref={graphRef} style={{
                width: '100%',
                overflow: 'hidden',
                resize: 'vertical',
                minHeight: menuNodesToGraph?.length || filters.graph ? '250px' : '0',
                maxHeight: menuNodesToGraph?.length || filters.graph ? '' : '0'
            }}>
                <div
                    style={{
                        height: '100%',
                        width: `${columnVirtualizer.getTotalSize()}px`,
                        position: 'relative',
                    }}
                >
                    <div style={{transform: `translateX(${graphXScroll.x}px)`, height: '100%'}}>
                        <UtilisationGraph menuNodesToRender={menuNodesToGraph} utilisationData={utilisationData}
                                          filters={filters} staff={staff} scroll={scroll}
                                          virtualColumns={graphXScroll.columns}
                                          menuNodeCfgSet={menuNodeCfgSet}
                                          setMenuNodeCfgSet={setMenuNodeCfgSet}

                        />
                    </div>
                </div>
            </div>
            <DndContext
                collisionDetection={closestCenter}
                sensors={sensors}
                modifiers={[restrictToFirstScrollableAncestor, restrictToVerticalAxis]}
                measuring={
                    {
                        draggable: {
                            measure: (el) => {
                                return getClientRect(el, {ignoreTransform: false});
                            }
                        },
                        droppable: {
                            // strategy: MeasuringStrategy.Always,
                            measure: (el) => {
                                // console.log(el);
                                // console.log(el.getBoundingClientRect());
                                const a =  getClientRect(el, {ignoreTransform: false})
                                // console.log(a);
                                return a;
                            }
                        }
                    }
                }
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                    <div className='scroll' ref={parentRef} style={{overflow: dragId ? 'hidden' : 'auto' , marginBottom: '30px'}}>
                        <div
                            style={{
                                height: `${rowVirtualizer.getTotalSize() + 23}px`,
                                width: `${columnVirtualizer.getTotalSize()}px`,
                                position: 'relative',
                            }}
                        >
                            <SortableContext
                                items={rowVirtualizer.getVirtualItems().map((item, i) => i)}
                                strategy={verticalListSortingStrategy}
                            >
                            {rowVirtualizer.getVirtualItems().map((virtualRow, i) =>
                                    <SortableUtilisationGridRow
                                        key={virtualRow.key}
                                        utilisationData={utilisationData}
                                        staff={staff}
                                        menuNodeCfgSet={menuNodeCfgSet}
                                        setMenuNodeCfgSet={setMenuNodeCfgSet}
                                        tasks={tasks}
                                        virtualRow={virtualRow}
                                        columnVirtualizer={columnVirtualizer}
                                        menuNodesFlatToRender={menuNodesFlatToRender}
                                        filters={filters}
                                        dragId={i}
                                        HEADING_COL_WIDTH={HEADING_COL_WIDTH}
                                        showClientTags={staffScheduleOnly}
                                    />
                            )}
                            </SortableContext>
                        </div>
                    </div>
                    <DragOverlay style={{height: '23px'}}>
                        {dragId ?
                            <UtilisationGridRow
                                key={rowVirtualizer.getVirtualItems()[dragId]?.key}
                                utilisationData={utilisationData}
                                staff={staff}
                                menuNodeCfgSet={menuNodeCfgSet}
                                setMenuNodeCfgSet={setMenuNodeCfgSet}
                                tasks={tasks}
                                virtualRow={rowVirtualizer.getVirtualItems()[dragId]}
                                columnVirtualizer={columnVirtualizer}
                                menuNodesFlatToRender={menuNodesFlatToRender}
                                HEADING_COL_WIDTH={HEADING_COL_WIDTH}
                                showClientTags={staffScheduleOnly}
                                filters={filters}
                                style={{opacity:0.5, color: 'grey'}}
                            />
                            : null}
                    </DragOverlay>
            </DndContext>
        </>
    )
    function handleDragStart(event) {
        const {active} = event;

        setDragId(active.id);
    }

    function handleDragEnd(event) {
        setDragId(null);
        const {active, over} = event;

        console.log(event);


        if (active.id !== over.id) {
            const activeMenuNode = menuNodesFlatToRender[rowVirtualizer.getVirtualItems()[active.id].index-1];
            const overMenuNode = menuNodesFlatToRender[rowVirtualizer.getVirtualItems()[over.id].index-1];

            if (!menuNodeCfgSet[overMenuNode.key]) menuNodeCfgSet[overMenuNode.key] = {};
            if (!menuNodeCfgSet[activeMenuNode.key]) menuNodeCfgSet[activeMenuNode.key] = {};
            const activeMenuNodeCfg = menuNodeCfgSet[activeMenuNode.key];
            const overMenuNodeCfg = menuNodeCfgSet[overMenuNode.key];

            console.log(activeMenuNode);
            console.log(overMenuNode);

            // let menuNodesInCurrentSortGroup = [];
            // menuNodesFlatToRender.every(menuNode => {
            //     if (activeMenuNode.rootLevelGroup === 'group') {
            //         if (menuNode.rootLevelGroup === 'group') menuNodesInCurrentSortGroup.push(menuNode);
            //     }
            //     if (activeMenuNode.rootLevelGroup === 'staff' && activeMenuNode.level == 1) {
            //        if (menuNode.rootLevelGroup === 'group' && menuNode.children.some(menuNode => menuNode.key === activeMenuNode.key)) {
            //            menuNodesInCurrentSortGroup = menuNode.children;
            //            return false;
            //        }
            //     }
            //     return true;
            // })
            const menuNodeKeysInCurrentSortGroup = [];
            menuNodesFlatToRender.forEach(menuNode => {
                // if (menuNode.rootLevelGroup === 'group' && menuNode.children.some(menuNode => menuNode.key === activeMenuNode.key)) {
                //     menuNodeKeysInCurrentSortGroup = menuNode.children.map(menuNode => menuNode.key);
                //     return false;
                // }
                if (menuNode.sortGroupKey === activeMenuNode.sortGroupKey) {
                    // menuNodeKeysInCurrentSortGroup = menuNode.children.map(menuNode => menuNode.key);
                    menuNodeKeysInCurrentSortGroup.push(menuNode.key);
                    // return false;
                }
                // return true;
            })

            console.log(menuNodeKeysInCurrentSortGroup);

            const oldIndex = menuNodeKeysInCurrentSortGroup.indexOf(activeMenuNode.key);
            const newIndex = menuNodeKeysInCurrentSortGroup.indexOf(overMenuNode.key);

            if (oldIndex === -1 || newIndex === -1) return;


            const sortedKeys = arrayMove(menuNodeKeysInCurrentSortGroup, oldIndex, newIndex);

            console.log(sortedKeys);

            // activeMenuNodeCfg.sort = sortedKeys.indexOf(overMenuNode.key);
            // if (overMenuNodeCfg.sort) overMenuNodeCfg.sort = sortedKeys.indexOf(activeMenuNodeCfg.sort);

            sortedKeys.forEach((key, i) => {
                if ((key in menuNodeCfgSet && 'sort' in menuNodeCfgSet[key]) || key === activeMenuNode.key || key === overMenuNode.key) {
                    menuNodeCfgSet[key].sort = i;
                }
            })


            setMenuNodeCfgSet({...menuNodeCfgSet});
        }
        //
    }
}
